import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, ChevronDown, ArrowRight, Clock } from "lucide-react";
import { StatCard } from "@/components/shared";
import { fadeInUp, staggerContainer, viewportOnce } from "@/constants";
import {
  PROPOSAL_LINK, IMPACT_STATS, OBJECTIVES, SKILL_GAP, LABS, COURSES, COMMUNITY,
  STAKEHOLDERS, ROLLOUT, INCLUDED, FAQS
} from "./data";
import { Architecture } from "./Architecture";
import { PHOTOS, FramedPhoto, CardImage } from "@/components/Photo";

const LAB_IMAGES = [PHOTOS.chip, PHOTOS.genai, PHOTOS.iot, PHOTOS.circuit, PHOTOS.security];
const STAKEHOLDER_IMAGES = [PHOTOS.boardroom, PHOTOS.lecture, PHOTOS.studentsLaptop, PHOTOS.team];

export const Block = ({ id, index, eyebrow, title, intro, children }) => (
  <motion.section
    id={id}
    initial="initial"
    whileInView="animate"
    viewport={viewportOnce}
    variants={staggerContainer}
    className="scroll-mt-32 py-16 md:py-20 border-b border-[#2A3648]/70 last:border-b-0"
    data-testid={`coe-section-${id}`}
  >
    <motion.div variants={fadeInUp} className="mb-10 max-w-3xl">
      <p className="text-[#C9A962] text-xs uppercase tracking-[0.25em] mb-3 flex items-center gap-3">
        <span className="font-mono">{index}</span><span className="h-px w-8 bg-[#C9A962]/50" />{eyebrow}
      </p>
      <h2 className="text-3xl md:text-4xl font-bold mb-4 leading-tight">{title}</h2>
      {intro && <p className="text-[#A0ABC0] text-base md:text-lg leading-relaxed">{intro}</p>}
    </motion.div>
    {children}
  </motion.section>
);

const Card = ({ children, className = "" }) => (
  <motion.div variants={fadeInUp} className={`p-6 bg-[#121E30] rounded-xl border border-[#2A3648] ${className}`}>{children}</motion.div>
);

export const Overview = () => (
  <Block id="overview" index="01" eyebrow="Overview" title="Data-centric labs that bridge academia and industry"
    intro="Intel Unnati is Intel India's technology-education initiative that broadens access to infrastructure and skills across higher education. As an authorized Intel Unnati partner, QCP — an ISO 27001:2022 certified, Govt. of India recognized Ed-Tech company — designs, sets up and runs your AI Centre of Excellence.">
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-12 items-center">
      <FramedPhoto src={PHOTOS.labTeacher} alt="Faculty guiding students in a computer lab" badge={{ value: "410+", label: "Unnati labs nationwide" }} testId="coe-overview-photo" />
      <div className="grid sm:grid-cols-2 gap-4">
      {["Build industry-ready talent in AI and data-centric computing", "Bridge the academia–industry gap with real use cases and mentorship", "Strengthen institutional capability with modern labs", "Foster an innovation and research culture on campus"].map((t) => (
        <Card key={t} className="flex items-start gap-3"><CheckCircle className="text-[#C9A962] flex-shrink-0 mt-0.5" size={18} /><p className="text-sm text-[#D0D6E0]">{t}</p></Card>
      ))}
      </div>
    </div>
  </Block>
);

export const Impact = () => (
  <Block id="impact" index="02" eyebrow="Mission & Impact" title="A national program with proven reach"
    intro="Launched on 21 October 2021, the program equips students with industry-relevant tech skills while increasing adoption of Intel technologies in academia.">
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
      {IMPACT_STATS.map((s) => <StatCard key={s.id} end={s.end} suffix={s.suffix} label={s.label} icon={s.icon} />)}
    </div>
    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
      {OBJECTIVES.map((o, i) => (
        <Card key={o.id}>
          <p className="font-mono text-[#C9A962] text-xs mb-3">OBJ-{i + 1}</p>
          <h4 className="font-bold mb-2">{o.title}</h4>
          <p className="text-[#A0ABC0] text-sm">{o.desc}</p>
        </Card>
      ))}
    </div>
    <motion.div variants={fadeInUp} className="grid sm:grid-cols-2 lg:grid-cols-4 rounded-xl overflow-hidden border border-[#C9A962]/25">
      {SKILL_GAP.map((g) => (
        <div key={g.id} className="p-6 bg-[#C9A962]/[0.04] border-b sm:border-r border-[#C9A962]/15 last:border-r-0">
          <p className="text-2xl font-bold text-[#C9A962] mb-1">{g.value}</p>
          <p className="text-[#A0ABC0] text-xs leading-relaxed">{g.label}</p>
        </div>
      ))}
    </motion.div>
  </Block>
);

export const Labs = () => (
  <Block id="labs" index="03" eyebrow="Technology Labs" title="Five lab tracks, configured to your campus"
    intro="Lab configurations align with your existing infrastructure and strategic objectives.">
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
      {LABS.map((l, i) => (
        <Card key={l.id} className="card-hover group overflow-hidden">
          <CardImage src={LAB_IMAGES[i]} alt={`${l.name} lab`} pad="p-6" />
          <div className="flex items-center gap-3 mb-4">
            <div className="icon-container w-11 h-11"><l.icon className="text-[#C9A962]" size={20} /></div>
            <div><p className="text-[10px] uppercase tracking-[0.2em] text-[#A0ABC0]">Intel Unnati</p><h4 className="font-bold">{l.name}</h4></div>
          </div>
          <ul className="space-y-2">
            {l.points.map((p) => <li key={p} className="text-sm text-[#A0ABC0] flex gap-2"><span className="text-[#C9A962]">—</span>{p}</li>)}
          </ul>
        </Card>
      ))}
    </div>
  </Block>
);

export const ArchitectureBlock = () => (
  <Block id="architecture" index="04" eyebrow="Architecture" title="Reference architecture of the Centre"
    intro="An expandable, modular design: a secured HPC / AI cluster serving student lab pods over a 10G fabric, with a layered software stack on top.">
    <motion.div variants={fadeInUp}><Architecture /></motion.div>
  </Block>
);

export const Curriculum = () => {
  const max = Math.max(...COURSES.map((c) => c.hours));
  return (
    <Block id="curriculum" index="05" eyebrow="Curriculum" title="Specially curated courses"
      intro="Industry-aligned content developed with faculty from leading institutions, delivered with hands-on lab exercises.">
      <div className="space-y-3">
        {COURSES.map((c) => (
          <Card key={c.id} className="!p-5">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center gap-3">
                <span className="text-[10px] px-2 py-0.5 rounded bg-[#C9A962]/10 text-[#C9A962] font-semibold uppercase tracking-wider">{c.track}</span>
                <h4 className="font-semibold text-sm md:text-base">{c.title}</h4>
              </div>
              <span className="text-xs text-[#A0ABC0] flex items-center gap-1"><Clock size={12} /> ~{c.hours} hrs</span>
            </div>
            <div className="h-1.5 rounded-full bg-[#0A1628] overflow-hidden mb-3">
              <motion.div initial={{ width: 0 }} whileInView={{ width: `${(c.hours / max) * 100}%` }} viewport={viewportOnce} transition={{ duration: 1 }} className="h-full bg-gradient-to-r from-[#C9A962] to-[#E8D5A3]" />
            </div>
            <p className="text-xs text-[#A0ABC0]">{c.highlights}</p>
          </Card>
        ))}
      </div>
    </Block>
  );
};

export const Community = () => (
  <Block id="community" index="06" eyebrow="Community & Events" title="An ecosystem beyond the lab"
    intro="Once part of Intel Unnati, your students and faculty gain year-round access to these initiatives.">
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {COMMUNITY.map((c) => (
        <Card key={c.id}>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A962] mb-2">{c.who}</p>
          <h4 className="font-bold mb-2">Intel Unnati {c.name}</h4>
          <p className="text-[#A0ABC0] text-sm">{c.note}</p>
        </Card>
      ))}
    </div>
  </Block>
);

export const Benefits = () => (
  <Block id="benefits" index="07" eyebrow="Benefits by Stakeholder" title="Value for everyone on campus">
    <div className="grid md:grid-cols-2 gap-4">
      {STAKEHOLDERS.map((s, i) => (
        <Card key={s.id} className="card-hover group overflow-hidden">
          <CardImage src={STAKEHOLDER_IMAGES[i]} alt={s.who} pad="p-6" />
          <div className="flex items-center gap-3 mb-4">
            <div className="icon-container w-11 h-11"><s.icon className="text-[#C9A962]" size={20} /></div>
            <h4 className="font-bold text-lg">{s.who}</h4>
          </div>
          <ul className="space-y-2.5">
            {s.points.map((p) => <li key={p} className="flex items-start gap-2 text-sm text-[#A0ABC0]"><CheckCircle className="text-[#C9A962] flex-shrink-0 mt-0.5" size={14} />{p}</li>)}
          </ul>
        </Card>
      ))}
    </div>
  </Block>
);

export const Rollout = () => (
  <Block id="rollout" index="08" eyebrow="Phased Rollout" title="From MoU to measurable outcomes"
    intro="Faculty enablement and delivery can run in person and/or virtually, based on your preferences.">
    <ol className="relative border-l border-[#C9A962]/30 ml-3 space-y-8">
      {ROLLOUT.map((r) => (
        <motion.li key={r.id} variants={fadeInUp} className="pl-8 relative" data-testid={`coe-rollout-${r.id}`}>
          <span className="absolute -left-[17px] top-0 w-8 h-8 rounded-full bg-[#0A1628] border border-[#C9A962] flex items-center justify-center">
            <r.icon size={14} className="text-[#C9A962]" />
          </span>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[#C9A962] mb-1">{r.phase}</p>
          <h4 className="font-bold mb-1">{r.title}</h4>
          <p className="text-[#A0ABC0] text-sm max-w-xl">{r.desc}</p>
        </motion.li>
      ))}
    </ol>
  </Block>
);

export const Included = () => (
  <Block id="included" index="09" eyebrow="What's Included" title="One partner, end to end"
    intro="Intel Unnati training, learning resources and certifications carry no program fees — the institution invests only in setting up the lab. Every engagement includes:">
    <div className="grid sm:grid-cols-2 gap-3">
      {INCLUDED.map((i) => (
        <motion.div key={i.id} variants={fadeInUp} className="flex items-center gap-3 p-4 rounded-lg bg-[#121E30] border border-[#2A3648]">
          <i.icon size={18} className="text-[#C9A962] flex-shrink-0" /><span className="text-sm">{i.text}</span>
        </motion.div>
      ))}
    </div>
  </Block>
);

const FaqItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-[#2A3648]">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 py-5 text-left" aria-expanded={open} data-testid={`coe-faq-${item.id}`}>
        <span className="font-semibold">{item.q}</span>
        <ChevronDown size={18} className={`text-[#C9A962] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <p className="overflow-hidden text-[#A0ABC0] text-sm leading-relaxed pr-8">{open && <span className="block pb-5">{item.a}</span>}</p>
      </div>
    </div>
  );
};

export const Faq = () => (
  <Block id="faq" index="10" eyebrow="FAQ" title="Frequently asked questions">
    <motion.div variants={fadeInUp}>{FAQS.map((f) => <FaqItem key={f.id} item={f} />)}</motion.div>
    <motion.div variants={fadeInUp} className="mt-12 p-8 rounded-xl border border-[#C9A962]/40 bg-[#C9A962]/[0.05] flex flex-col md:flex-row md:items-center justify-between gap-6">
      <div>
        <h3 className="text-xl font-bold mb-1">Ready for a tailored proposal?</h3>
        <p className="text-[#A0ABC0] text-sm">Share a few details and verify your email — our team will send investment options for your campus.</p>
      </div>
      <Link to={PROPOSAL_LINK} className="btn-primary inline-flex items-center gap-2 whitespace-nowrap" data-testid="coe-faq-proposal-cta">
        Request a Proposal <ArrowRight size={18} />
      </Link>
    </motion.div>
  </Block>
);
